import { useCustomerPricingSync } from '~/hooks/use-customer-pricing-sync';
import { useClientNavigation } from '~/utils/live-host-provider';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from '@remix-run/react';
import type { LinksFunction, LoaderFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { StoreProvider } from '~/context/store-context';
import { getSelectedStore } from '~/cookies';
import { hasHeaderSessionHint } from '~/utils/header-session';
import {
  getSiteTimezoneFromCentral,
  getStoreSchedulesFromCentral,
  getStoresFromCentral,
} from '~/api/store-central.server';
import React, { useEffect } from 'react';
import useGTM from '~/hooks/useGTM';
import useGTMPageView from '~/components/track-page-view';
import useCrazyEgg from '../hooks/useCrazyEgg';
import useThoughtmetric from '../hooks/useThoughtmetric';
import { useTokenRefresh } from '~/hooks/use-token-refresh';
import PromotionHandler from '~/components/promotion-handler';

import './tailwind.css';
// Page-builder base styles. Global on purpose: per-page `generated_css` is
// baked by the builder and layers on top, but the base rules (marquee
// keyframes, reveal layers, hide-when-empty) must be present on every builder
// page regardless of when that page was last saved.
import './styles/puck-render.css';
import { SpeedInsights } from '@vercel/speed-insights/remix';

export const links: LinksFunction = () => [
  { rel: 'apple-touch-icon', sizes: '180x180', href: '/assets/icons/apple-touch-icon.png' },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '192x192',
    href: '/assets/icons/android-chrome-192x192.png',
  },
  { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/assets/icons/favicon-32x32.png' },
  { rel: 'icon', type: 'image/png', sizes: '96x96', href: '/assets/icons/favicon-96x96.png' },
  { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/assets/icons/favicon-16x16.png' },

  // Font preconnects
  // Google Fonts (Karla + Gothic A1) — used by the product grid's price badge
  // and the review modal, and by nothing above the fold on most pages.
  //
  // NOT RENDER-BLOCKING, since 2026-09-16. As a plain stylesheet link this sat
  // on the critical path: DNS + TLS to a third-party origin, then ~39 KB of CSS
  // (of which Lighthouse measured 33 KB unused), before the browser would paint
  // anything at all. It was the largest single item in that run's
  // "render-blocking requests, est. savings 1,010 ms".
  //
  // `media="print"` makes the browser fetch it at low priority without blocking
  // the render; an effect below flips it to `all` after hydration. The families
  // already carry `display=swap`, so text has always rendered in the fallback
  // first — this changes WHEN the swap happens, not whether.
  //
  // The flip is an EFFECT and not an `onLoad` prop, which was the first attempt
  // and is silently broken: React does not serialise event handlers into SSR
  // HTML, so it attaches `onLoad` at hydration — long after the stylesheet has
  // finished loading — and the handler never fires. The link would have sat on
  // `media="print"` forever and Karla / Gothic A1 would simply never apply.
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Karla:wght@400;500;700&family=Gothic+A1:wght@300;400;500&display=swap',
    media: 'print',
  },
  // Image domain preconnects for better product image loading performance
  { rel: 'preconnect', href: 'https://catalyst-images-budflow.s3.us-west-1.amazonaws.com' },
  // Removed: catalyst-staging.niagra.io (placeholder images now local)
  // Removed: rocketcdn.me (daily deals banners were dead code)
  { rel: 'preconnect', href: 'https://d14jfwlo68bhyr.cloudfront.net' },
  // WordPress preconnect removed — all content migrated to Supabase/CloudFront

  // Critical third-party domain preconnects for performance
  { rel: 'preconnect', href: 'https://stapecdn.com' },
  { rel: 'preconnect', href: 'https://app.jointcommerce.com' },
  { rel: 'preconnect', href: 'https://assets.mantisadnetwork.com' },
  { rel: 'preconnect', href: 'https://cdn.surfside.io' },
  { rel: 'preconnect', href: 'https://res.cloudinary.com' },
  { rel: 'preconnect', href: 'https://pixel.thoughtmetric.io' },

  // DNS prefetch fallbacks for broader browser support
  { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
  { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
  { rel: 'dns-prefetch', href: 'https://catalyst-images-budflow.s3.us-west-1.amazonaws.com' },
  // Removed: catalyst-staging.niagra.io and rocketcdn.me DNS prefetch (no longer used)
  { rel: 'dns-prefetch', href: 'https://d14jfwlo68bhyr.cloudfront.net' },
  // WordPress DNS prefetch removed — all content migrated to Supabase/CloudFront
  { rel: 'dns-prefetch', href: 'https://stapecdn.com' },
  { rel: 'dns-prefetch', href: 'https://app.jointcommerce.com' },
  { rel: 'dns-prefetch', href: 'https://assets.mantisadnetwork.com' },
  { rel: 'dns-prefetch', href: 'https://cdn.surfside.io' },
  { rel: 'dns-prefetch', href: 'https://res.cloudinary.com' },
  { rel: 'dns-prefetch', href: 'https://pixel.thoughtmetric.io' },

  // ✅ MINIMAL FONT PRELOADS - Only core fonts used site-wide
  // Other fonts load on-demand via @font-face to eliminate unused preload warnings
  {
    rel: 'preload',
    href: '/assets/fonts/FuturaStdBook.otf',
    as: 'font',
    type: 'font/otf',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preload',
    href: '/assets/fonts/FuturaStdBold.otf',
    as: 'font',
    type: 'font/otf',
    crossOrigin: 'anonymous',
  },

  // Local fonts don't need external preconnects
];

/**
 * Routes where `?store=` already means something other than "set my store",
 * and so must be left alone by the handler below.
 *
 *   /thank-you/$ticketId — identifies the store the ORDER was placed at and is
 *     passed to getTreezTicket(). Consuming it would query the wrong store and
 *     404 the page on a conversion event.
 *   /checkout — `storeFromQuery || cookieStore`, and the pickup-store switcher
 *     re-loads the route as `/checkout?store=X` to price against another store
 *     without changing the visitor's own selection.
 *
 * Both are post-cart pages that Google Business Profile never links to, so
 * excluding them costs nothing and keeps this change clear of the order flow.
 * `/product/$slug` also reads the param, but only as a fallback AFTER the
 * cookie, so it behaves identically once the cookie has been set.
 */
const STORE_PARAM_RESERVED_PATHS = ['/checkout', '/thank-you'];

export const loader: LoaderFunction = async ({ request }) => {
  const cookieStore = getSelectedStore(request);
  const stores = await getStoresFromCentral();
  // Same in-memory Store Central cache as `stores` — no extra upstream call.
  // Feeds the store picker's open / closing soon / closed labels.
  const [scheduleRows, siteTimezone] = await Promise.all([
    getStoreSchedulesFromCentral(),
    getSiteTimezoneFromCentral(),
  ]);
  const storeSchedules = Object.fromEntries(scheduleRows.map(({ id, schedule }) => [id, schedule]));
  const validStore = stores.find((s) => s.value === cookieStore && !s.comingSoon);
  const store = validStore?.value || '';

  // `?store=<slug>` pre-selects a store, so each Google Business Profile listing
  // can point its "Order online" link straight at that location's menu:
  //   /shop?store=catalystbellflower&utm_source=google&utm_medium=organic&...
  //
  // This only validates and hands the result to the client — it deliberately
  // sets no cookie and issues no redirect, so a request that isn't a GBP arrival
  // is byte-for-byte unchanged from before.
  //
  // Two checks, both required. Sanitise first (alphanumerics and hyphens only),
  // then validate against the store directory, accepting only a shoppable store
  // (`comingSoon === false`). The value ends up in a 1-year cookie, so without
  // validation any `?store=<anything>` could park a visitor in a store that does
  // not exist or has not opened — an empty menu, persisted for a year. A new
  // store's GBP listing typically goes live before the store does, so that is a
  // realistic route in, not just hand-typed URLs. Anything failing either check
  // is ignored and the visitor falls through to the normal cookie flow.
  const url = new URL(request.url);
  const storeParam = url.searchParams.get('store');
  const isReservedPath = STORE_PARAM_RESERVED_PATHS.some((path) => url.pathname.startsWith(path));

  const storeFromUrl =
    storeParam &&
    !isReservedPath &&
    /^[a-zA-Z0-9-]+$/.test(storeParam) &&
    stores.some((s) => s.value === storeParam && !s.comingSoon)
      ? storeParam
      : null;

  // Expose the full store list to all child routes via useRouteLoaderData('root').
  // The 7 consumers (header selector, age gate, mobile sidebar, checkout, etc.)
  // can read it from there instead of importing from ~/constants/stores.
  return json(
    {
      store,
      storeFromUrl,
      stores,
      storeSchedules,
      siteTimezone,
      hasSessionHint: hasHeaderSessionHint(request.headers.get('Cookie')),
    },
    { headers: { 'Cache-Control': 'private, no-store' } },
  );
};

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  // Builder links navigate in the app; focus + announcement on every new page
  // (utils/live-host-provider.tsx, live states §13.7).
  const prefetchLinks = useClientNavigation();

  // Only preload hero background image on pages that actually use it
  const pagesWithHeroImage = [
    '/', // homepage - uses both hero images
    // Remove other pages that don't actually use these specific hero images
  ];
  const needsHeroImage = pagesWithHeroImage.includes(location.pathname);

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Image Preload] Page: ${location.pathname}, NeedsHeroImage: ${needsHeroImage}`);
  }

  // Only load GTM in production to improve dev performance.
  // Eager-load on /thank-you/* — the hard checkout redirect leaves a fresh
  // document with no JS state, and users often close the tab before the
  // 8s deferred load fires, dropping the purchase event entirely.
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    const isThankYou = location.pathname.startsWith('/thank-you/');
    useGTM({ eager: isThankYou });
  }

  // ✅ IMMEDIATE FONT RENDERING - No waiting for slow external fonts
  // Critical fonts load in ~100ms, external fonts can load in background
  useEffect(() => {
    // Add fonts-loaded class immediately for fast rendering
    document.documentElement.classList.add('fonts-loaded');

    // Optional: Log when all fonts eventually finish (for debugging only)
    if (process.env.NODE_ENV === 'development') {
      document.fonts.ready.then(() => {
        console.log('🎉 All fonts eventually loaded at:', performance.now(), 'ms');
      });
    }
  }, []);

  // Handle deferred script loading for performance
  useEffect(() => {
    // Defer Klaviyo and FontAwesome loading for better performance
    const loadKlaviyo = () => {
      // Skip Klaviyo in development to prevent performance warnings
      if (process.env.NODE_ENV === 'development') {
        console.log('[Klaviyo] Script loading disabled in development mode');
        return;
      }

      if (document.querySelector('script[src*="klaviyo"]')) return;

      const script = document.createElement('script');
      script.async = true;
      script.type = 'text/javascript';
      script.src = '//static.klaviyo.com/onsite/js/RnfC9N/klaviyo.js';
      document.head.appendChild(script);
    };

    // Promote the Google Fonts stylesheet from `media="print"` (how it ships, so
    // it never blocks first paint) to `all`, now that we are past hydration and
    // first paint has happened. See the links() comment for why this is not an
    // onLoad prop.
    const gf = document.querySelector<HTMLLinkElement>(
      'link[rel="stylesheet"][href*="fonts.googleapis.com"][media="print"]',
    );
    if (gf) gf.media = 'all';

    // ✅ FontAwesome - Load after user interaction to prevent blocking
    const loadFontAwesome = () => {
      if (document.querySelector('script[src*="fontawesome"]')) return;

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://kit.fontawesome.com/1c677307d8.js';
      document.head.appendChild(script);
    };

    // Load scripts after meaningful user interaction or after 10 seconds
    const events = ['click', 'keydown', 'touchstart'];
    const autoLoad = () => {
      events.forEach((event) => document.removeEventListener(event, loadKlaviyo, true));
      loadKlaviyo();
      loadFontAwesome(); // ✅ Load after user interaction to prevent blocking
    };

    events.forEach((event) => document.addEventListener(event, autoLoad, true));
    const klaviyoTimer = setTimeout(autoLoad, 10000);

    return () => {
      clearTimeout(klaviyoTimer); // Klaviyo timer
      events.forEach((event) => document.removeEventListener(event, autoLoad, true));
    };
  }, []);
  useGTMPageView();

  // Only load analytics in production to improve dev performance
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    useCrazyEgg();
    useThoughtmetric();
  }

  return (
    <html className="scroll-smooth" lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="google-site-verification" content="j1aWMXFVqizgr9MgFuDrOpyDQGo4oUsmW-aX-q7CMXg" />
        <Meta />
        <Links />

        {/*
          Organization and WebSite markup, site-wide.

          Google sources the site name shown beside the favicon in search results
          from this, falling back to og:site_name. Without it the name is inferred,
          which is why "Catalyst Cannabis" was being repeated inside every page
          title to make sure it appeared at all — this states it once, properly,
          and lets the title spend its 60 characters on the product instead.

          @id lets the two nodes reference each other and gives later structured
          data (product, breadcrumb, local business) something stable to hang off.
        */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'Organization',
                  '@id': 'https://catalyst-cannabis.com/#organization',
                  name: 'Catalyst Cannabis',
                  url: 'https://catalyst-cannabis.com',
                  logo: {
                    '@type': 'ImageObject',
                    url: 'https://catalyst-cannabis.com/logos/Catalyst-Logo.webp',
                  },
                  sameAs: ['https://x.com/Catalyst_WFTP'],
                },
                {
                  '@type': 'WebSite',
                  '@id': 'https://catalyst-cannabis.com/#website',
                  name: 'Catalyst Cannabis',
                  url: 'https://catalyst-cannabis.com',
                  publisher: { '@id': 'https://catalyst-cannabis.com/#organization' },
                },
              ],
            }),
          }}
        />

        {/* Initialize dataLayer early so pageview events are captured before GTM loads */}
        {/* Push dispensary_id immediately so the Google Tag has it for the first page_view */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer=window.dataLayer||[];
(function(){var m=document.cookie.match(/(?:^|; )selectedStore=([^;]*)/);var s=m?decodeURIComponent(m[1]):'';if(s)window.dataLayer.push({dispensary_id:s});})();`,
          }}
        />

        {/* ✅ Critical above-the-fold images preload - mobile optimized */}
        {needsHeroImage && (
          <>
            <link
              rel="preload"
              href="/images/welcome-to-catalyst-california-dispensaries.webp"
              as="image"
              {...({ fetchpriority: 'high' } as any)}
            />
            <link
              rel="preload"
              href="/images/catalyst-light-bg-stoner-mobile-optimized.webp"
              as="image"
              {...({ fetchpriority: 'high' } as any)}
            />
          </>
        )}

        {/* ✅ Local Fonts - Poppins now loaded via @font-face in tailwind.css */}

        {/* Font loading optimization - prevent layout shifts only for components using these fonts */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
              /* Only optimize fonts where they're explicitly used to avoid affecting other pages */
              
              /* Poppins optimization - only for .font-poppins class */
              @supports (size-adjust: 100%) {
                @font-face {
                  font-family: 'Poppins Fallback';
                  src: local('Arial'), local('Helvetica'), local('system-ui');
                  size-adjust: 107%;
                  ascent-override: 105%;
                  descent-override: 35%;
                  line-gap-override: 10%;
                }
              }
              
              .font-poppins {
                font-family: 'Poppins', 'Poppins Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                font-optical-sizing: auto;
                font-feature-settings: 'kern' 1;
                text-rendering: optimizeSpeed;
              }
              
              /* Helvetica Neue optimization - only for .font-helvetica-neue class */
              @supports (size-adjust: 100%) {
                @font-face {
                  font-family: 'Helvetica Neue Fallback';
                  src: local('Arial'), local('Helvetica'), local('system-ui');
                  size-adjust: 100%;
                  ascent-override: 92%;
                  descent-override: 23%;
                  line-gap-override: 0%;
                }
              }
              
              .font-helvetica-neue {
                font-family: 'Helvetica Neue', 'Helvetica Neue Fallback', 'Helvetica', Arial, system-ui, sans-serif;
                text-rendering: optimizeSpeed;
              }
              
              /* Futura optimization - only for .font-futura class */
              @supports (size-adjust: 100%) {
                @font-face {
                  font-family: 'Futura Fallback';
                  src: local('Arial Black'), local('Helvetica Bold'), local('system-ui');
                  size-adjust: 95%;
                  ascent-override: 88%;
                  descent-override: 22%;
                  line-gap-override: 0%;
                  font-weight: 400;
                }
                
                @font-face {
                  font-family: 'Futura Fallback';
                  src: local('Arial Black'), local('Helvetica Bold'), local('system-ui');
                  size-adjust: 95%;
                  ascent-override: 88%;
                  descent-override: 22%;
                  line-gap-override: 0%;
                  font-weight: 700;
                }
              }
              
              .font-futura {
                font-family: 'Futura Std', 'Futura Fallback', 'Arial Black', 'Helvetica Bold', Arial, system-ui, sans-serif;
                text-rendering: optimizeSpeed;
              }
            `,
          }}
        />

        {/* Local fonts don't need noscript fallbacks */}

        {/* ✅ Klaviyo tracking script - deferred for performance */}
        {/* Without JavaScript the observer never runs: keep animated content visible. */}
        <noscript>
          <style>{`.pb-anim-ready [data-pb-animate]:not(.pb-in-view) { opacity: 1 !important; transform: none !important; }`}</style>
          {/* A scheduled builder element outside its window (budflow-skunk SCHEDULING_PLAN.md §2.3):
              the renderers write `hidden` + `data-pb-hidden`; an author's own `display` would beat
              `[hidden]`, so the attribute is backed here as well as in the page's baked CSS. */}
          <style>{`[data-pb-hidden] { display: none !important; }`}</style>
        </noscript>
      </head>
      {/* `pb-anim-ready` arms the hidden start of scroll entrances until the
          route observer marks them in view — MIRROR of embarc root.tsx. It was
          never set here although use-builder-page-runtime says it is, so a
          Catalyst entrance showed at rest, jumped to its start and animated
          (found 2026-10-12, states plan slice E). */}
      <body className="pb-anim-ready">
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-W4F4XCR4"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        {children}
        {prefetchLinks}
        <ScrollRestoration />
        <Scripts />
        <SpeedInsights />
      </body>
    </html>
  );
}

function App() {
  const { store, storeFromUrl } = useLoaderData<{ store: string; storeFromUrl: string | null }>();

  // Phase 6: keep the logged-in budflow session alive (rotate access_token
  // before its ~1h expiry) so personalisation + cart ownership don't silently
  // drop to guest mid-session.
  useTokenRefresh();
  useCustomerPricingSync();

  // Apply a validated `?store=` arrival (see the loader above).
  //
  // Writes exactly what store-selector.tsx:217-220 writes when someone picks a
  // store from the selector — same two keys, same values — so the rest of the
  // app, the cart included, sees a state it already handles today. Nothing here
  // runs unless storeFromUrl is set, which means a visitor who did not arrive
  // from a GBP link executes no new code at all.
  useEffect(() => {
    if (!storeFromUrl) return;

    localStorage.setItem('selectedStore', storeFromUrl);
    document.cookie = `selectedStore=${storeFromUrl}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;

    // Returning visitor: drop `?store=` from the URL and reload, so loaders
    // re-run against the new cookie. Every other param is kept, so the UTMs
    // survive to the pageview GTM actually reports — GTM is deferred 8s
    // (useGTM.ts:44), well past this redirect, so no pageview fires twice.
    //
    // New visitor: no redirect. The age gate is about to open and needs to
    // complete on its own terms; it reads this store as its pre-selection and
    // mints the Treez token itself on confirm, exactly as it does for a
    // visitor who picks a store by hand.
    if (document.cookie.includes('ageApproved=true')) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('store');
      window.location.replace(`${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }
  }, [storeFromUrl]);

  // Seed from the URL when present so the correct store renders on the first
  // paint rather than flashing the cookie's store before the redirect.
  return (
    <StoreProvider initialStore={storeFromUrl || store}>
      <PromotionHandler />
      <Outlet />
    </StoreProvider>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

export default withSentry(App);
